import React, { useState, useEffect, useRef, useCallback, Fragment } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { dashboardToast } from "../../utils/dashboardToast";
import "bootstrap/dist/css/bootstrap.min.css";

import Menu from "../components/menu";
import DashboardShell from "../../components/DashboardShell";
import { useMenuState } from "../../Common/MenuStateContext";
import SaListPageShell from "../../components/sa/SaListPageShell";
import ContentLoader from "../../components/ContentLoader";
import RecordDetailModal from "../../components/RecordDetailModal";
import TableSortSelect from "../../components/TableSortSelect";
import { Alert, Button as UiButton, Pagination as UiPagination } from "../../components/ui";
import { TABLE_SORT } from "../../utils/tableSort";
import { getBusinessValueDrivers, addOrUpdateBusinessValueDriver, deleteBusinessValueDriver } from "../../services/CostEstimationsetupApi";
import { ChevronDown, ChevronRight, DollarSign, LineChart, Pencil, Plus, Search, Trash2 } from "lucide-react";
import "../../assets/css/dashboard.css";

const EmptyCell = () => <span className="app-td__empty">—</span>;

const BusinessValueDrivers = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [viewMode, setViewMode] = useState("parent"); // "parent" | "child"
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(""); // "businessValueDriver" or "subDriver"
  const [expandedRows, setExpandedRows] = useState(new Set()); // Track expanded business value driver rows

  // Business Value Drivers form data
  const [businessValueDriverForm, setBusinessValueDriverForm] = useState({
    id: 0,
    name: "",
    description: "",
    isActive: true,
    businessSubValueDrivers: [] // Updated to match API response structure
  });

  // API data for Business Value Drivers
  const [businessValueDrivers, setBusinessValueDrivers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState(TABLE_SORT.DATE_DESC);
  const [isActiveFilter, setIsActiveFilter] = useState(true);
  const dataFetchedRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleMenuSelection = useCallback((item) => {
    setSelectedItem(item);
    return item;
  }, []);

  const [apiPageInfo, setApiPageInfo] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalPages: 1,
    totalRecords: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });

  // API call function
  const fetchBusinessValueDrivers = useCallback(async (page = apiPageInfo.pageNumber, pageSize = apiPageInfo.pageSize, sort = sortKey, search = searchTerm, isActive = isActiveFilter) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getBusinessValueDrivers({
        searchTerm: search,
        isActive,
        page,
        pageSize,
        sort,
      });

      if (response && response.data) {
        setBusinessValueDrivers(response.data.data || []);
        setApiPageInfo(prev => ({
          ...prev,
          pageNumber: page,
          pageSize,
          totalRecords: response.data.totalRecords || 0,
          totalPages: Math.max(1, response.data.totalPages || 1),
          hasNextPage: response.data.hasNextPage || false,
          hasPreviousPage: response.data.hasPreviousPage || false
        }));
      } else {
        setBusinessValueDrivers([]);
      }
    } catch (err) {
      console.error('Error fetching business value drivers:', err);
      setError(err.message || 'Failed to fetch business value drivers');
      dashboardToast.error(err.message || 'Failed to fetch business value drivers');
      setBusinessValueDrivers([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, isActiveFilter, apiPageInfo.pageNumber, apiPageInfo.pageSize, sortKey]);

  // Load data on component mount
  useEffect(() => {
    fetchBusinessValueDrivers(apiPageInfo.pageNumber, apiPageInfo.pageSize);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!dataFetchedRef.current) {
      dataFetchedRef.current = true;
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      fetchBusinessValueDrivers(1, apiPageInfo.pageSize, sortKey, searchTerm, isActiveFilter);
    }, 300);

    return () => window.clearTimeout(timerId);
    // eslint-disable-next-line
  }, [searchTerm]);

  // Business Value Drivers handlers
  const handleShowBusinessValueDriver = useCallback((businessValueDriver = null) => {
    // Clear sub-driver form when showing business value driver form
    setBusinessValueDriverForm({
      id: 0,
      name: "",
      description: "",
      isActive: true,
      businessSubValueDrivers: [] // Clear sub-drivers on new form
    });

    if (businessValueDriver) {
      setBusinessValueDriverForm({
        id: businessValueDriver.id,
        name: businessValueDriver.name || "",
        description: businessValueDriver.description || "",
        isActive: businessValueDriver.isActive !== undefined ? businessValueDriver.isActive : true,
        businessSubValueDrivers: businessValueDriver.businessSubValueDrivers || [] // Load sub-drivers if available
      });
    } else {
      setBusinessValueDriverForm({
        id: 0,
        name: "",
        description: "",
        isActive: true,
        businessSubValueDrivers: []
      });
    }
    setShowModal(true);
  }, []);

  const handleClose = useCallback(() => {
    setShowModal(false);
    // Reset both forms when closing modal
    setBusinessValueDriverForm({
      id: 0,
      name: "",
      description: "",
      isActive: true,
      businessSubValueDrivers: []
    });
  }, []);

  const handleBusinessValueDriverChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setBusinessValueDriverForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleAddSubDriverField = useCallback(() => {
    setBusinessValueDriverForm(prev => ({
      ...prev,
      businessSubValueDrivers: [...prev.businessSubValueDrivers, { name: "", description: "", isActive: true }]
    }));
  }, []);

  const handleRemoveSubDriverField = useCallback((index) => {
    setBusinessValueDriverForm(prev => {
      const newSubDrivers = [...prev.businessSubValueDrivers];
      newSubDrivers.splice(index, 1);
      return { ...prev, businessSubValueDrivers: newSubDrivers };
    });
  }, []);

  const handleSubDriverFieldChange = useCallback((index, field, value) => {
    setBusinessValueDriverForm(prev => ({
      ...prev,
      businessSubValueDrivers: prev.businessSubValueDrivers.map((subDriver, i) =>
        i === index ? { ...subDriver, [field]: value } : subDriver
      )
    }));
  }, []);

  const handleBusinessValueDriverSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { name, description, isActive, businessSubValueDrivers } = businessValueDriverForm;
      if (name) {
        try {
          setLoading(true);
          
          // For editing existing business value driver, handle sub-driver deletions
          if (businessValueDriverForm.id > 0) {
            // Get the original sub-drivers from the loaded data
            const originalBusinessValueDriver = businessValueDrivers.find(bvd => bvd.id === businessValueDriverForm.id);
            const originalSubDrivers = originalBusinessValueDriver?.businessSubValueDrivers || [];
            
            // Find sub-drivers that were removed (exist in original but not in current form)
            const currentSubDriverIds = businessSubValueDrivers
              .filter(subDriver => subDriver.id && subDriver.id > 0)
              .map(subDriver => subDriver.id);
            
            const removedSubDrivers = originalSubDrivers.filter(
              originalSub => !currentSubDriverIds.includes(originalSub.id)
            );
            
            // Delete removed sub-drivers
            for (const removedSubDriver of removedSubDrivers) {
              try {
                await deleteBusinessValueDriver(removedSubDriver.id, true);
              } catch (deleteError) {
                console.error('Error deleting sub-driver:', deleteError);
                // Continue with the main update even if sub-driver deletion fails
              }
            }
          }
          
          // Prepare the data for API
          const apiData = {
            id: businessValueDriverForm.id,
            name: name.trim(),
            description: description ? description.trim() : '',
            isActive: isActive,
            businessSubValueDrivers: businessSubValueDrivers
              .filter(subDriver => subDriver.name && subDriver.name.trim()) // Only include sub-drivers with names
              .map(subDriver => ({
                id: subDriver.id || 0,
                businessValueDriverId: businessValueDriverForm.id || 0,
                name: subDriver.name.trim(),
                description: subDriver.description ? subDriver.description.trim() : '',
                isActive: subDriver.isActive !== undefined ? subDriver.isActive : true
              }))
          };

          const response = await addOrUpdateBusinessValueDriver(apiData);
          
          if (response && response.succeeded !== false) {
            dashboardToast.success(
              businessValueDriverForm.id > 0 
                ? "Business Value Driver updated successfully" 
                : "Business Value Driver created successfully"
            );
            handleClose();
            // Refresh the data
            fetchBusinessValueDrivers();
          } else {
            dashboardToast.error(response?.message || "Failed to save Business Value Driver");
          }
        } catch (err) {
          console.error(err);
          dashboardToast.error(err.message || "Failed to save Business Value Driver");
        } finally {
          setLoading(false);
        }
      } else {
        dashboardToast.error("Please fill in all required fields");
      }
    },
    [businessValueDriverForm, handleClose, fetchBusinessValueDrivers, businessValueDrivers]
  );

  const handleDelete = useCallback((item, type) => {
    setItemToDelete(item);
    setDeleteType(type);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    try {
      setLoading(true);
      
      if (deleteType === "businessValueDriver") {
        // Delete main Business Value Driver
        const response = await deleteBusinessValueDriver(itemToDelete.id, false);
        
        if (response && (response.success !== false || response.succeeded !== false)) {
          dashboardToast.success("Business Value Driver and related Sub Value Drivers deleted successfully");
          // Remove from expanded rows
          setExpandedRows(prev => {
            const newSet = new Set(prev);
            newSet.delete(itemToDelete.id);
            return newSet;
          });
          // Refresh the data
          fetchBusinessValueDrivers();
        } else {
          dashboardToast.error(response?.message || "Failed to delete Business Value Driver");
        }
      } else {
        // Delete sub-driver
        const response = await deleteBusinessValueDriver(itemToDelete.id, true);
        
        if (response && (response.success !== false || response.succeeded !== false)) {
          dashboardToast.success("Sub Value Driver deleted successfully");
          // Refresh the data
          fetchBusinessValueDrivers();
        } else {
          dashboardToast.error(response?.message || "Failed to delete Sub Value Driver");
        }
      }
    } catch (err) {
      console.error(err);
      dashboardToast.error(err.message || "Failed to delete item");
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setItemToDelete(null);
      setDeleteType("");
    }
  }, [itemToDelete, deleteType, fetchBusinessValueDrivers]);

  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setItemToDelete(null);
    setDeleteType("");
  }, []);

  const openViewModal = useCallback((record, mode = "parent") => {
    setViewRecord(record);
    setViewMode(mode);
    setShowViewModal(true);
  }, []);

  const closeViewModal = useCallback(() => {
    setShowViewModal(false);
    setViewRecord(null);
    setViewMode("parent");
  }, []);

  const goToPage = useCallback(n => {
    const pg = Math.min(Math.max(n, 1), apiPageInfo.totalPages);
    if (pg !== apiPageInfo.pageNumber) {
      fetchBusinessValueDrivers(pg, apiPageInfo.pageSize);
    }
  }, [apiPageInfo.pageNumber, apiPageInfo.totalPages, apiPageInfo.pageSize, fetchBusinessValueDrivers]);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleSortChange = (nextSort) => {
    setSortKey(nextSort);
    fetchBusinessValueDrivers(1, apiPageInfo.pageSize, nextSort, searchTerm, isActiveFilter);
  };

  const handleActiveFilterChange = useCallback((e) => {
    const checked = e.target.checked;
    setIsActiveFilter(checked);
    fetchBusinessValueDrivers(1, apiPageInfo.pageSize, sortKey, searchTerm, checked);
  }, [apiPageInfo.pageSize, fetchBusinessValueDrivers, sortKey, searchTerm]);

  const handleRefresh = useCallback(() => {
    fetchBusinessValueDrivers();
  }, [fetchBusinessValueDrivers]);

  // Toggle row expansion
  const toggleRowExpansion = (businessValueDriverId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(businessValueDriverId)) {
        newSet.delete(businessValueDriverId);
      } else {
        newSet.add(businessValueDriverId);
      }
      return newSet;
    });
  };

  // Get sub drivers for a specific Business Value Driver
  const getSubDrivers = (businessValueDriver) => {
    return businessValueDriver.businessSubValueDrivers || [];
  };

  // Get sub drivers count for a Business Value Driver
  const getSubDriversCount = (businessValueDriver) => {
    return (businessValueDriver.businessSubValueDrivers || []).length;
  };

  return (
    <DashboardShell
      isMenuCollapsed={isMenuCollapsed}
      sidebar={
        <Menu
          onSelect={handleMenuSelection}
          selectedItem={selectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />
      }
    >
      <SaListPageShell topBarTitle="Business Value Drivers Management">
        <div className="dash-frame-parent1">
          <div className="dash-list-toolbar">
            <div className="dash-list-toolbar__intro">
              <h5 className="dash-list-toolbar__title">Manage Business Value Drivers</h5>
              <p className="dash-list-toolbar__desc">
                Create and manage business value drivers and their sub-drivers.
              </p>
            </div>
            <div className="dash-list-toolbar__actions">
              <div className="dash-search-field dash-table-search">
                <Search className="dash-search-field__icon" aria-hidden />
                <input
                  type="text"
                  placeholder="Search by name…"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="dash-search-field__input"
                  disabled={loading}
                />
              </div>
              <TableSortSelect variant="chip"
                value={sortKey}
                onChange={handleSortChange}
                disabled={loading}
              />
              <Form.Check
                type="checkbox"
                label="Active only"
                checked={isActiveFilter}
                onChange={handleActiveFilterChange}
                className="dash-filter-check"
              />
              <UiButton
                variant="primary"
                className="sa-create-btn"
                onClick={() => handleShowBusinessValueDriver()}
              >
                Create Driver
              </UiButton>
            </div>
          </div>

          {loading ? (
            <ContentLoader label="Loading business value drivers…" />
          ) : error ? (
            <Alert variant="error">{error}</Alert>
          ) : (
            <Fragment>
              <div className="app-table-wrap app-table-wrap--refined">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th aria-label="Expand" />
                      <th>#</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {businessValueDrivers.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center">
                          <span className="app-td__desc">No business value drivers found.</span>
                        </td>
                      </tr>
                    ) : (
                      businessValueDrivers.map((businessValueDriver, index) => {
                        const subDriversList = getSubDrivers(businessValueDriver);
                        const isExpanded = expandedRows.has(businessValueDriver.id);
                        const displayName = businessValueDriver.name || "Untitled driver";

                        return (
                          <Fragment key={businessValueDriver.id}>
                            <tr
                              className="rdm-row-clickable"
                              onClick={() => openViewModal(businessValueDriver, "parent")}
                            >
                              <td onClick={(e) => e.stopPropagation()}>
                                {subDriversList.length > 0 ? (
                                  <button
                                    type="button"
                                    className="vd-expand-btn"
                                    onClick={() => toggleRowExpansion(businessValueDriver.id)}
                                    title={isExpanded ? "Collapse sub-drivers" : "Expand sub-drivers"}
                                    aria-expanded={isExpanded}
                                    aria-label={
                                      isExpanded
                                        ? `Collapse ${displayName}`
                                        : `Expand ${displayName}`
                                    }
                                  >
                                    {isExpanded ? <ChevronDown aria-hidden /> : <ChevronRight aria-hidden />}
                                  </button>
                                ) : null}
                              </td>
                              <td>
                                <span className="app-td__index">{index + 1}</span>
                              </td>
                              <td>
                                <span className="app-td__title">{displayName}</span>
                              </td>
                              <td>
                                {businessValueDriver.description ? (
                                  <span className="app-td__desc" title={businessValueDriver.description}>
                                    {businessValueDriver.description}
                                  </span>
                                ) : (
                                  <EmptyCell />
                                )}
                              </td>
                              <td>
                                <span className="app-td__meta">Business</span>
                              </td>
                              <td>
                                <span
                                  className={
                                    businessValueDriver.isActive
                                      ? "app-status app-status--success"
                                      : "app-status app-status--muted"
                                  }
                                >
                                  {businessValueDriver.isActive ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td
                                className="dash-mobile-table-actions"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="btn-list-actions">
                                  <UiButton
                                    variant="outline"
                                    size="sm"
                                    className="btn-list-edit"
                                    title="Edit"
                                    aria-label={`Edit ${displayName}`}
                                    onClick={() => handleShowBusinessValueDriver(businessValueDriver)}
                                  >
                                    <Pencil />
                                  </UiButton>
                                  <UiButton
                                    variant="danger"
                                    size="sm"
                                    className="btn-list-delete"
                                    title="Delete"
                                    aria-label={`Delete ${displayName}`}
                                    onClick={() =>
                                      handleDelete(businessValueDriver, "businessValueDriver")
                                    }
                                  >
                                    <Trash2 />
                                  </UiButton>
                                </div>
                              </td>
                            </tr>

                            {isExpanded
                              ? subDriversList.length > 0
                                ? subDriversList.map((subDriver) => (
                                    <tr
                                      key={`sub-${subDriver.id}`}
                                      className="rdm-row-clickable vd-sub-row"
                                      onClick={() => openViewModal(subDriver, "child")}
                                    >
                                      <td />
                                      <td />
                                      <td>
                                        <span className="app-td__desc vd-sub-name">
                                          {subDriver.name}
                                        </span>
                                      </td>
                                      <td>
                                        {subDriver.description ? (
                                          <span className="app-td__desc" title={subDriver.description}>
                                            {subDriver.description}
                                          </span>
                                        ) : (
                                          <EmptyCell />
                                        )}
                                      </td>
                                      <td>
                                        <span className="app-td__meta">Sub-driver</span>
                                      </td>
                                      <td>
                                        <span
                                          className={
                                            subDriver.isActive
                                              ? "app-status app-status--success"
                                              : "app-status app-status--muted"
                                          }
                                        >
                                          {subDriver.isActive ? "Active" : "Inactive"}
                                        </span>
                                      </td>
                                      <td>
                                        <EmptyCell />
                                      </td>
                                    </tr>
                                  ))
                                : (
                                    <tr className="vd-sub-row">
                                      <td colSpan={7} className="text-center">
                                        <span className="app-td__desc">No sub-drivers yet</span>
                                      </td>
                                    </tr>
                                  )
                              : null}
                          </Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <UiPagination
                page={apiPageInfo.pageNumber}
                totalPages={apiPageInfo.totalPages}
                onPageChange={goToPage}
              />
            </Fragment>
          )}
        </div>
      </SaListPageShell>

      {/* Create/Edit Modal */}
      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        size="lg"
        dialogClassName="rounded-3"
      >
        <Modal.Header closeButton className="dashboard-modal-header">
          {businessValueDriverForm.id > 0 ? (
            <>
              <DollarSign className="h-5 w-5 me-2" />
              <Modal.Title>Edit Business Value Driver</Modal.Title>
            </>
          ) : (
            <>
              <DollarSign className="h-5 w-5 me-2" />
              <Modal.Title>Create Business Value Driver</Modal.Title>
            </>
          )}
        </Modal.Header>

        {businessValueDriverForm.id > 0 ? (
          <Form onSubmit={handleBusinessValueDriverSubmit}>
            <Modal.Body className="px-4">
              <Row>
                <Col md={6}>
                  <Form.Group controlId="formName" className="mb-2">
                    <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={businessValueDriverForm.name}
                      onChange={handleBusinessValueDriverChange}
                      required
                      placeholder="Enter business value driver name"
                      maxLength="100"
                      style={{ height: "38px" }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="formDescription" className="mb-2">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      type="text"
                      name="description"
                      value={businessValueDriverForm.description}
                      onChange={handleBusinessValueDriverChange}
                      placeholder="Enter description"
                      maxLength="200"
                      style={{ height: "38px" }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group controlId="formIsActive" className="mb-0">
                    <Form.Label>Status</Form.Label>
                    <div className="mt-0">
                      <Form.Check
                        type="checkbox"
                        name="isActive"
                        checked={businessValueDriverForm.isActive}
                        onChange={handleBusinessValueDriverChange}
                        label="Active"
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              {/* Sub Driver Creation Section */}
              <div className="d-flex justify-content-between align-items-center mt-2 mb-1">
                <h6 className="mb-0">
                  <LineChart className="me-2" />
                  Add Sub Value Drivers
                </h6>
                <Button
                  onClick={() => handleAddSubDriverField()}
                  className="btn btn-dashboard-primary"
                  size="sm"
                >
                  <Plus className="me-1" />
                  Add More
                </Button>
              </div>

              {/* Sub Driver Fields */}
              {businessValueDriverForm.businessSubValueDrivers && businessValueDriverForm.businessSubValueDrivers.map((subDriver, index) => (
                <div key={index} className="border rounded p-1 mb-1" style={{ backgroundColor: "#f8f9fa" }}>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <h6 className="mb-0">Sub Value Driver {index + 1}</h6>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="cap-mgmt-icon-btn"
                      onClick={() => handleRemoveSubDriverField(index)}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                  <Row>
                    <Col md={6}>
                      <Form.Group controlId={`subDriverName${index}`} className="mb-1">
                        <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={subDriver.name}
                          onChange={(e) => handleSubDriverFieldChange(index, 'name', e.target.value)}
                          placeholder="Enter sub value driver name"
                          maxLength="100"
                          style={{ height: "38px" }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId={`subDriverDescription${index}`} className="mb-1">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                          type="text"
                          value={subDriver.description}
                          onChange={(e) => handleSubDriverFieldChange(index, 'description', e.target.value)}
                          placeholder="Enter description"
                          maxLength="200"
                          style={{ height: "38px" }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group controlId={`subDriverIsActive${index}`} className="mb-1">
                        <Form.Label>Status</Form.Label>
                        <div className="mt-1">
                          <Form.Check
                            type="checkbox"
                            checked={subDriver.isActive !== undefined ? subDriver.isActive : true}
                            onChange={(e) => handleSubDriverFieldChange(index, 'isActive', e.target.checked)}
                            label="Active"
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              ))}
            </Modal.Body>

            <Modal.Footer>
              <Button variant="outline-secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn btn-dashboard-primary"
              >
                Save Business Value Driver
              </Button>
            </Modal.Footer>
          </Form>
        ) : (
          <Form onSubmit={handleBusinessValueDriverSubmit}>
            <Modal.Body className="px-4">
              <Row>
                <Col md={6}>
                  <Form.Group controlId="formName" className="mb-2">
                    <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={businessValueDriverForm.name}
                      onChange={handleBusinessValueDriverChange}
                      required
                      placeholder="Enter business value driver name"
                      maxLength="100"
                      style={{ height: "38px" }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="formDescription" className="mb-2">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      type="text"
                      name="description"
                      value={businessValueDriverForm.description}
                      onChange={handleBusinessValueDriverChange}
                      placeholder="Enter description"
                      maxLength="200"
                      style={{ height: "38px" }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group controlId="formIsActive" className="mb-0">
                    <Form.Label>Status</Form.Label>
                    <div className="mt-0">
                      <Form.Check
                        type="checkbox"
                        name="isActive"
                        checked={businessValueDriverForm.isActive}
                        onChange={handleBusinessValueDriverChange}
                        label="Active"
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              {/* Sub Driver Creation Section */}
              <div className="d-flex justify-content-between align-items-center mt-2 mb-1">
                <h6 className="mb-0">
                  <LineChart className="me-2" />
                  Add Sub Value Drivers
                </h6>
                <Button
                  onClick={() => handleAddSubDriverField()}
                  className="btn btn-dashboard-primary"
                  size="sm"
                >
                  <Plus className="me-1" />
                  Add More
                </Button>
              </div>

              {/* Sub Driver Fields */}
              {businessValueDriverForm.businessSubValueDrivers && businessValueDriverForm.businessSubValueDrivers.map((subDriver, index) => (
                <div key={index} className="border rounded p-1 mb-1" style={{ backgroundColor: "#f8f9fa" }}>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <h6 className="mb-0">Sub Value Driver {index + 1}</h6>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="cap-mgmt-icon-btn"
                      onClick={() => handleRemoveSubDriverField(index)}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                  <Row>
                    <Col md={6}>
                      <Form.Group controlId={`subDriverName${index}`} className="mb-1">
                        <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={subDriver.name}
                          onChange={(e) => handleSubDriverFieldChange(index, 'name', e.target.value)}
                          placeholder="Enter sub value driver name"
                          maxLength="100"
                          style={{ height: "38px" }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId={`subDriverDescription${index}`} className="mb-1">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                          type="text"
                          value={subDriver.description}
                          onChange={(e) => handleSubDriverFieldChange(index, 'description', e.target.value)}
                          placeholder="Enter description"
                          maxLength="200"
                          style={{ height: "38px" }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group controlId={`subDriverIsActive${index}`} className="mb-1">
                        <Form.Label>Status</Form.Label>
                        <div className="mt-1">
                          <Form.Check
                            type="checkbox"
                            checked={subDriver.isActive !== undefined ? subDriver.isActive : true}
                            onChange={(e) => handleSubDriverFieldChange(index, 'isActive', e.target.checked)}
                            label="Active"
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              ))}
            </Modal.Body>

            <Modal.Footer>
              <Button variant="outline-secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn btn-dashboard-primary"
              >
                Save Business Value Driver
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>

      {/* Delete Confirmation */}
      <Modal show={showDeleteModal} onHide={cancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{" "}
          <strong>{itemToDelete?.name}</strong>?
          {deleteType === "businessValueDriver" && (
            <div className="alert alert-warning mt-2">
              <strong>Warning:</strong> This will also delete all related Sub Value Drivers.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelDelete}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <RecordDetailModal
        show={showViewModal}
        onHide={closeViewModal}
        title={
          viewRecord?.name ||
          (viewMode === "child"
            ? "Sub Value Driver Details"
            : "Business Value Driver Details")
        }
        subtitle={
          viewMode === "child"
            ? "Sub value driver record"
            : "Business value driver record"
        }
        fields={
          viewMode === "child"
            ? [
                { label: "Name", value: viewRecord?.name },
                {
                  label: "Description",
                  value: viewRecord?.description,
                  fullWidth: true,
                },
                { label: "Type", value: "Sub Value Driver" },
                { label: "Active", value: Boolean(viewRecord?.isActive) },
              ]
            : [
                { label: "Name", value: viewRecord?.name },
                {
                  label: "Description",
                  value: viewRecord?.description,
                  fullWidth: true,
                },
                { label: "Type", value: "Business Value Driver" },
                { label: "Active", value: Boolean(viewRecord?.isActive) },
              ]
        }
        groups={
          viewMode === "parent"
            ? [
                {
                  title: "Sub Value Drivers",
                  content: (() => {
                    const children = viewRecord ? getSubDrivers(viewRecord) : [];
                    if (!children.length) {
                      return <span className="rdm-empty">None</span>;
                    }
                    return children.map((sub) => (
                      <div key={sub.id} className="rdm-nested-card">
                        <div className="rdm-nested-card-title">{sub.name}</div>
                        {sub.description ? (
                          <div className="rdm-nested-meta">{sub.description}</div>
                        ) : null}
                        <div className="rdm-nested-meta">
                          {sub.isActive ? "Active" : "Inactive"}
                        </div>
                      </div>
                    ));
                  })(),
                },
              ]
            : []
        }
        actions={
          viewMode === "parent"
            ? [
                {
                  type: "edit",
                  onClick: () => {
                    const record = viewRecord;
                    closeViewModal();
                    if (record) handleShowBusinessValueDriver(record);
                  },
                },
                {
                  type: "delete",
                  onClick: () => {
                    const record = viewRecord;
                    closeViewModal();
                    if (record) handleDelete(record, "businessValueDriver");
                  },
                },
              ]
            : []
        }
      />

      {/* ToastContainer moved to App.js */}
    </DashboardShell>
  );
};

export default BusinessValueDrivers;
